/**
 * BESTIX v5.0 — How It Works
 * Step-by-step guide for buyers and sellers
 */

import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

const buyerSteps = [
  { number: 1, title: 'Search & Discover', description: 'Browse events or use search to find what you need' },
  { number: 2, title: 'Select Tickets', description: 'Choose seats, compare prices, view availability' },
  { number: 3, title: 'Secure Checkout', description: 'Complete purchase with instant ticket delivery' },
];

const sellerSteps = [
  { number: 1, title: 'List Your Tickets', description: 'Upload details and set your price' },
  { number: 2, title: 'We Handle Everything', description: 'Secure payment and delivery' },
  { number: 3, title: 'Get Paid Fast', description: 'Receive funds within 24 hours' },
];

interface HowItWorksProps {
  className?: string;
}

export function HowItWorks({ className }: HowItWorksProps) {
  return (
    <section className={cn('py-20 px-6', className)}>
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16">
          {/* For Buyers */}
          <div>
            <div className="mb-8">
              <div className="w-12 h-px bg-[var(--color-navy)] mb-4" />
              <h2 className="font-playfair italic text-2xl text-[var(--color-navy)]">For Buyers</h2>
              <p className="text-gray-500 mt-1">Simple, secure, and fast ticket purchasing</p>
            </div>
            <div className="space-y-6">
              {buyerSteps.map((step) => (
                <div key={step.number} className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--color-gold)] flex items-center justify-center text-[var(--color-navy)] font-bold">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="font-medium text-[var(--color-navy)]">{step.title}</h3>
                    <p className="text-gray-500 text-sm mt-1">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link 
              to="/events" 
              className="inline-block mt-8 px-6 py-3 bg-[var(--color-navy)] text-white rounded-full font-medium hover:bg-[var(--color-navy-light)] transition-colors"
            >
              Browse Events
            </Link>
          </div>

          {/* For Sellers */}
          <div>
            <div className="mb-8">
              <div className="w-12 h-px bg-[var(--color-navy)] mb-4" />
              <h2 className="font-playfair italic text-2xl text-[var(--color-navy)]">For Sellers</h2>
              <p className="text-gray-500 mt-1">Sell your tickets quickly and safely</p>
            </div>
            <div className="space-y-6">
              {sellerSteps.map((step) => (
                <div key={step.number} className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--color-gold)] flex items-center justify-center text-[var(--color-navy)] font-bold">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="font-medium text-[var(--color-navy)]">{step.title}</h3>
                    <p className="text-gray-500 text-sm mt-1">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link 
              to="/sell" 
              className="inline-block mt-8 px-6 py-3 bg-white text-[var(--color-navy)] border-2 border-[var(--color-navy)] rounded-full font-medium hover:bg-gray-50 transition-colors"
            >
              Start Selling
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HowItWorks;
